import { INVOICE_STATUS } from '../constants/statusEnums';

/**
 * Accounts-receivable predicates for invoices — ONE definition.
 *
 * WHY THIS FILE EXISTS (F-IRIS-0813-2226 / F-ATLAS-0816-0005-1, fixed 2026-08-18):
 * "Is this invoice a receivable?" was implemented twice. The Invoices page filter
 * (`pages/Invoices.js`) required `status !== draft/cancelled/void`; the stat cards
 * that DRIVE that filter re-derived their own predicate per card and two of them
 * omitted it. So a card counted rows its own click could never show:
 *
 *   Jerry's 48f32d34, live 2026-08-18 — Overdue card 28, its filter 14 (exactly
 *   double). Smoke c8c96c4b — aging Current $4,947.60 across 17 drafts, filter 0 rows.
 *
 * Aging excludes drafts because a draft invoice has not been issued, so it is not
 * a receivable — the same rule QuickBooks, Jobber and Housecall Pro apply, and the
 * same rule this DB's own `invoice_aging_report` view encodes (`WHERE status =
 * 'invoiced'`). It is not a UI preference.
 *
 * Add a new AR-shaped card? Use these. Do not re-derive the predicate inline —
 * that is precisely how the two copies drifted apart.
 */

// Statuses that are NOT receivables: never issued (draft), or withdrawn (cancelled/void).
// Deliberately NOT including `written_off` or `disputed`: both are real enum values but
// zero rows carry them live, and treating a written-off invoice as non-collectible is a
// change to what AR MEANS — an operator call, not a bug fix. Kept identical to the
// predicate pages/Invoices.js already used, so this only makes the cards agree with it.
export const NON_RECEIVABLE_INVOICE_STATUSES = [
  INVOICE_STATUS.DRAFT,
  INVOICE_STATUS.CANCELLED,
  INVOICE_STATUS.VOID
];

// #5540: a paid or withdrawn invoice has nothing to send — "Resend" on a VOID invoice emailed the
// customer again (InvoiceSendingService sends and only skips the status change). One rule for the
// detail modal AND the list's row buttons; the list kept offering "Review & send" on void,
// cancelled and written-off rows after the modal was fixed (verified live 2026-10-14).
export const UNSENDABLE_INVOICE_STATUSES = [
  INVOICE_STATUS.PAID,
  INVOICE_STATUS.VOID,
  INVOICE_STATUS.CANCELLED,
  INVOICE_STATUS.WRITTEN_OFF
];
export const canSendInvoice = (invoice) => !UNSENDABLE_INVOICE_STATUSES.includes(invoice?.status);

/** Amounts below this are treated as settled (floating-point cents guard). */
export const SETTLED_BALANCE_EPSILON = 0.005;

/**
 * Sent to the customer and still standing — paid or not. Drafts were never sent; void and
 * cancelled were withdrawn. Collection Rate summed drafts and voids into "invoiced" (Feb 2026
 * read 18.8% against 100% of what was actually issued; audit 2026-10-17).
 */
export const isIssuedInvoice = (invoice) =>
  ![INVOICE_STATUS.DRAFT, INVOICE_STATUS.VOID, INVOICE_STATUS.CANCELLED]
    .includes(String(invoice?.status || '').toLowerCase());

/** An invoice that has been issued and not withdrawn. */
export const isActiveInvoice = (invoice) =>
  !NON_RECEIVABLE_INVOICE_STATUSES.includes(invoice?.status);

/**
 * What the customer still owes.
 *
 * `??` not `||`: balance_due of 0 means SETTLED and must be respected. The cards used
 * `balance_due || total_amount`, so a settled invoice fell through to its full total and
 * was reported as entirely outstanding. Live 2026-08-18: one `sent` invoice with
 * balance_due = 0 and total_amount 141.56 was counted as $141.56 owed. Same falsy-zero
 * class as CLAUDE.md §15's tax/markup rule.
 */
export const invoiceBalance = (invoice) =>
  Number(
    invoice?.balance_due ??
      ((invoice?.total_amount || 0) - (invoice?.amount_paid || 0))
  ) || 0;

/**
 * Issued, not withdrawn, NOT already settled, and still owing — the set every AR total
 * is drawn from.
 *
 * PAID is excluded explicitly, and that is not the same test as `balance > 0`. Live
 * 2026-08-19 on 48f32d34 there are three invoices with `status = 'paid'` AND a real
 * balance: INV-10025 (total 750.00, amount_paid 0.00, balance 750.00), INV-10026
 * (450.00 of 750.00) and INV-10023 (654.92 of 979.92) — all with paid_at set. Those rows
 * are internally inconsistent and are reported separately; what matters here is that an
 * invoice the contractor has marked PAID must not reappear in an unpaid list, an aging
 * bucket or a cash forecast. Showing a row labelled "Paid" inside "Sent Unpaid" reads as
 * a broken screen, not as a data warning.
 *
 * The pre-consolidation aging code tested `status !== PAID && balance > 0`. That test was
 * lost when these predicates were unified and is restored here.
 */
export const isReceivable = (invoice) =>
  isActiveInvoice(invoice) &&
  invoice?.status !== INVOICE_STATUS.PAID &&
  invoiceBalance(invoice) > SETTLED_BALANCE_EPSILON;

/**
 * Is this invoice overdue? THE definition — the same one the server uses.
 *
 * get_invoice_kpis computes its Overdue tile as `is_receivable AND days_overdue > 0`.
 * InvoicesTable had its own rule that was only the SECOND half: status===OVERDUE, or not
 * paid and past due_date. It never asked whether the invoice was a receivable, so a DRAFT
 * past its due date wore a red OVERDUE pill on a row the Overdue tile above it — correctly —
 * did not count. Two rules for one concept on one screen, reported by the operator as
 * "drift on overdue" (F-8).
 *
 * `status === OVERDUE` is deliberately NOT a shortcut here. It is a real enum value but a
 * draft or cancelled row carrying it still is not money owed, and the receivable test
 * already covers the legitimate case: an invoice marked overdue is issued, unpaid and has a
 * balance. Overdue is DERIVED — see the note at pages/Invoices.js:1228, where reading the
 * literal enum showed 0 next to a tile reading 11.
 *
 * `isPastDue` is injected rather than imported so this module stays free of the date/
 * timezone layer; callers pass utils/dateFormatters' company-timezone-aware version.
 */
export const isInvoiceOverdue = (invoice, isPastDue) => {
  if (!isReceivable(invoice)) return false;
  if (!invoice?.due_date) return false;
  return isPastDue(invoice.due_date);
};
